import React, { useId, useMemo, useState } from 'react';
import { format } from 'date-fns';
import type { AttendanceTrendPoint } from '../../types/playerCard';
import { ATTENDANCE_AT_RISK_THRESHOLD } from '../../services/playerAttendanceService';

interface AttendanceTrendChartProps {
  trend: AttendanceTrendPoint[];
}

const WIDTH = 640;
const HEIGHT = 200;
const PADDING = { top: 16, right: 16, bottom: 28, left: 36 };

/**
 * Single-series line chart of the team's attendance % over its most recent
 * logged events. No chart library in this codebase, so this is a small
 * self-contained SVG chart (see the `dataviz` skill for the spec this follows:
 * 2px line, ≥8px markers with a surface ring, recessive hairline gridlines,
 * a crosshair+tooltip on hover, and a table fallback for accessibility).
 *
 * Single series → no legend needed (the heading names what's plotted).
 * No dark-mode variant: this app has no dark theme today (see e.g.
 * AttendanceLog.tsx), so this chart matches that fixed light styling.
 */
export function AttendanceTrendChart({ trend }: AttendanceTrendChartProps) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);
  const gradientId = useId();

  const plotWidth = WIDTH - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;

  const points = useMemo(() => {
    if (trend.length === 0) return [];
    return trend.map((point, index) => {
      const x = trend.length === 1
        ? PADDING.left + plotWidth / 2
        : PADDING.left + (index / (trend.length - 1)) * plotWidth;
      const y = PADDING.top + plotHeight - (point.attendance_percentage / 100) * plotHeight;
      return { ...point, x, y };
    });
  }, [trend, plotWidth, plotHeight]);

  if (trend.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Attendance Trend</h3>
        <p className="text-sm text-gray-500">No practices or games logged yet.</p>
      </div>
    );
  }

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ');
  const thresholdY = PADDING.top + plotHeight - (ATTENDANCE_AT_RISK_THRESHOLD / 100) * plotHeight;
  const hovered = hoverIndex !== null ? points[hoverIndex] : null;

  const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relativeX = ((e.clientX - rect.left) / rect.width) * WIDTH;
    let nearest = 0;
    let nearestDist = Infinity;
    points.forEach((p, i) => {
      const dist = Math.abs(p.x - relativeX);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = i;
      }
    });
    setHoverIndex(nearest);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Attendance Trend</h3>
          <p className="text-sm text-gray-500 mt-0.5">Last {trend.length} practices &amp; games</p>
        </div>
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          className="text-sm text-primary hover:underline"
          aria-label={showTable ? 'Show trend as chart' : 'Show trend as table'}
        >
          {showTable ? 'Show chart' : 'View as table'}
        </button>
      </div>

      {showTable ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-4 font-medium">Date</th>
                <th className="py-2 pr-4 font-medium">Type</th>
                <th className="py-2 font-medium">Attendance %</th>
              </tr>
            </thead>
            <tbody>
              {trend.map((point) => (
                <tr key={`${point.event_type}-${point.event_date}`} className="border-b border-gray-100">
                  <td className="py-2 pr-4 text-gray-900">{format(new Date(point.event_date), 'MMM d, yyyy')}</td>
                  <td className="py-2 pr-4 text-gray-600 capitalize">{point.event_type}</td>
                  <td
                    className={`py-2 font-medium ${
                      point.attendance_percentage < ATTENDANCE_AT_RISK_THRESHOLD ? 'text-red-600' : 'text-gray-900'
                    }`}
                  >
                    {point.attendance_percentage}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="relative">
          <svg
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            className="w-full h-auto touch-none"
            role="img"
            aria-label={`Attendance trend line chart across the last ${trend.length} events`}
            onPointerMove={handlePointerMove}
            onPointerLeave={() => setHoverIndex(null)}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2e8b57" stopOpacity={0.12} />
                <stop offset="100%" stopColor="#2e8b57" stopOpacity={0} />
              </linearGradient>
            </defs>

            {/* Gridlines — recessive hairlines at 0/50/100% */}
            {[0, 50, 100].map((tick) => {
              const y = PADDING.top + plotHeight - (tick / 100) * plotHeight;
              return (
                <g key={tick}>
                  <line x1={PADDING.left} y1={y} x2={WIDTH - PADDING.right} y2={y} stroke="#e7e5e4" strokeWidth={1} />
                  <text x={PADDING.left - 8} y={y} textAnchor="end" dominantBaseline="middle" className="fill-stone-500" fontSize={10}>
                    {tick}%
                  </text>
                </g>
              );
            })}

            {/* At-risk threshold reference line */}
            <line
              x1={PADDING.left}
              y1={thresholdY}
              x2={WIDTH - PADDING.right}
              y2={thresholdY}
              stroke="#f87171"
              strokeWidth={1}
              strokeDasharray="4 4"
            />

            {/* Area wash under the line */}
            <path d={`${linePath} L ${points[points.length - 1].x} ${PADDING.top + plotHeight} L ${points[0].x} ${PADDING.top + plotHeight} Z`} fill={`url(#${gradientId})`} />

            {/* The line itself */}
            <path d={linePath} fill="none" stroke="#2e8b57" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

            {/* Crosshair */}
            {hovered && (
              <line x1={hovered.x} y1={PADDING.top} x2={hovered.x} y2={PADDING.top + plotHeight} stroke="#a8a29e" strokeWidth={1} />
            )}

            {/* Markers — at-risk points in red, everything else in the series color */}
            {points.map((p, i) => {
              const atRisk = p.attendance_percentage < ATTENDANCE_AT_RISK_THRESHOLD;
              const isLast = i === points.length - 1;
              const isHovered = hoverIndex === i;
              return (
                <g key={`${p.event_type}-${p.event_date}`}>
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={isHovered ? 6 : 4}
                    fill={atRisk ? '#ef4444' : '#2e8b57'}
                    stroke="#ffffff"
                    strokeWidth={2}
                  />
                  {/* Larger transparent hit target for hover/focus, per interaction spec */}
                  <circle cx={p.x} cy={p.y} r={12} fill="transparent" />
                  {isLast && (
                    <text x={p.x} y={p.y - 12} textAnchor="middle" className="fill-stone-700" fontSize={11} fontWeight={600}>
                      {p.attendance_percentage}%
                    </text>
                  )}
                </g>
              );
            })}
          </svg>

          {hovered && (
            <div
              className="absolute pointer-events-none bg-gray-900 text-white text-xs rounded px-2 py-1.5 shadow-lg -translate-x-1/2"
              style={{
                left: `${(hovered.x / WIDTH) * 100}%`,
                top: `${(hovered.y / HEIGHT) * 100}%`,
                marginTop: '-2.5rem',
              }}
            >
              <div className="font-semibold">{hovered.attendance_percentage}% attendance</div>
              <div className="text-gray-300 capitalize">
                {hovered.event_type} · {format(new Date(hovered.event_date), 'MMM d, yyyy')}
              </div>
            </div>
          )}

          <p className="text-xs text-stone-500 mt-2">
            Dashed line marks the at-risk threshold ({ATTENDANCE_AT_RISK_THRESHOLD}%).
          </p>
        </div>
      )}
    </div>
  );
}

export default AttendanceTrendChart;
